/**
 * PHASE 6 — simulated device orientation for desktop development.
 *
 * The whole app expects an OrientationSource (duck type from phase 4). On a
 * real device that is the browser's deviceorientation events; on a desktop
 * (no sensors) this class lets the user "look around" with the mouse:
 *
 *   drag right  -> yaw right (heading/camera pans east)
 *   drag up     -> pitch up (camera rises above the horizon)
 *
 * It emits standard deviceorientationabsolute events, so it plugs into
 * OrientationFusion without any changes. `step()` is deterministic (used by
 * tests); `start()` drives it with requestAnimationFrame while emitting —
 * but only where rAF exists.
 *
 * Pose model: the device is held upright (beta = 90 deg) with the view axis
 * pointing at {yaw, pitch}. Yaw 0 = north, positive = east (matching the
 * astronomy engine's azimuth). Verified against the W3C conversion:
 *   alpha = (360 - yaw) mod 360,  beta = 90 + pitch,  gamma = 0.
 */

import type { OrientationEventLike, OrientationSource } from "../orientation/orientationFusion";
import type { OrientationReading } from "../orientation/deviceOrientation";

export const PITCH_MIN = -75;
export const PITCH_MAX = 85;
export const YAW_LIMIT = 180;

export interface SimulatedReading extends OrientationReading {
  absolute: true;
}

/**
 * Pure pose -> reading synthesis (unit-testable). clamps yaw to [-180, 180]
 * and pitch to [-75, 85] (a phone-beyond-vertical or below-horizon pose is
 * fine, but keep the sim in a sane range).
 */
export function syntheticReading(yawDeg: number, pitchDeg: number, timestampMs = 0): SimulatedReading {
  const yaw = clamp(yawDeg, -YAW_LIMIT, YAW_LIMIT);
  const pitch = clamp(pitchDeg, PITCH_MIN, PITCH_MAX);
  return {
    alphaDeg: (360 - yaw) % 360,
    betaDeg: 90 + pitch,
    gammaDeg: 0,
    absolute: true,
    timestampMs,
  };
}

export interface SimulatedOrientationOptions {
  /** Degrees of camera rotation per pixel of drag. Default 0.25. */
  sensitivity?: number;
  /** Blend factor applied per frame, in [0, 1]. Default 0.2. */
  smoothing?: number;
  /** Initial pose. Default { yawDeg: 0, pitchDeg: 0 } (facing north). */
  initial?: { yawDeg: number; pitchDeg: number };
}

export class SimulatedOrientation implements OrientationSource {
  private sens: number;
  private lerp: number;
  private targetYaw: number;
  private targetPitch: number;
  private currentYaw: number;
  private currentPitch: number;
  private time = 0;
  private listeners = new Set<(e: OrientationEventLike) => void>();
  private rafId: number | null = null;
  private lastFrameMs = 0;

  constructor(options: SimulatedOrientationOptions = {}) {
    this.sens = options.sensitivity ?? 0.25;
    this.lerp = clamp(options.smoothing ?? 0.2, 0, 1);
    const init = options.initial ?? { yawDeg: 0, pitchDeg: 0 };
    this.targetYaw = this.currentYaw = init.yawDeg;
    this.targetPitch = this.currentPitch = init.pitchDeg;
  }

  addEventListener(type: "deviceorientation" | "deviceorientationabsolute", cb: (e: OrientationEventLike) => void): void {
    if (type === "deviceorientationabsolute") this.listeners.add(cb);
  }

  removeEventListener(type: "deviceorientation" | "deviceorientationabsolute", cb: (e: OrientationEventLike) => void): void {
    if (type === "deviceorientationabsolute") this.listeners.delete(cb);
  }

  /** Apply a mouse drag (accumulated deltas). Returns the new target pose. */
  drag(dxPx: number, dyPx: number): { yawDeg: number; pitchDeg: number } {
    this.targetYaw = clamp(this.targetYaw + dxPx * this.sens, -YAW_LIMIT, YAW_LIMIT);
    this.targetPitch = clamp(this.targetPitch - dyPx * this.sens, PITCH_MIN, PITCH_MAX);
    return this.targets();
  }

  reset(pose?: { yawDeg: number; pitchDeg: number }): void {
    const init = pose ?? { yawDeg: 0, pitchDeg: 0 };
    this.targetYaw = this.currentYaw = init.yawDeg;
    this.targetPitch = this.currentPitch = init.pitchDeg;
  }

  targets(): { yawDeg: number; pitchDeg: number } {
    return { yawDeg: this.targetYaw, pitchDeg: this.targetPitch };
  }

  /** Ease current pose toward the target and emit one reading. Deterministic. */
  step(dtMs = 16): void {
    this.time += dtMs;
    this.currentYaw += (this.targetYaw - this.currentYaw) * this.lerp;
    this.currentPitch += (this.targetPitch - this.currentPitch) * this.lerp;
    const reading = syntheticReading(this.currentYaw, this.currentPitch, this.time);
    const event: OrientationEventLike = {
      alpha: reading.alphaDeg,
      beta: reading.betaDeg,
      gamma: reading.gammaDeg,
      absolute: true,
      timeStamp: reading.timestampMs,
    };
    for (const listener of this.listeners) listener(event);
  }

  start(): void {
    if (this.rafId !== null || typeof requestAnimationFrame !== "function") return;
    this.lastFrameMs = performance.now();
    const loop = (nowMs: number): void => {
      this.step(Math.max(1, nowMs - this.lastFrameMs));
      this.lastFrameMs = nowMs;
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.rafId === null) return;
    cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  get listenerCount(): number {
    return this.listeners.size;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}